/**
 * TokenNudgeBanner
 *
 * Displayed as a thin strip inside the editor when the user has consumed ≥ 80 %
 * of their monthly LLM budget.  Hidden for:
 *   - BYOK users (they pay their own provider; platform cap is irrelevant)
 *   - Unlimited / admin users (llm_cap_cents === -1)
 *   - Users who already dismissed it this calendar month
 *
 * The banner auto-refreshes every 5 minutes so it disappears by itself if the
 * user tops-up their wallet mid-session.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, TrendingUp, X } from 'lucide-react';
import { usageApi } from '@/lib/api';

const STORAGE_PREFIX = 'cnr_token_nudge_v1_';

function monthKey() {
  return new Date().toISOString().slice(0, 7); // "2026-02"
}

export function TokenNudgeBanner() {
  const navigate = useNavigate();
  const [usage,     setUsage]     = useState(null);
  const [dismissed, setDismissed] = useState(
    () => !!localStorage.getItem(STORAGE_PREFIX + monthKey()),
  );

  const load = useCallback(async () => {
    try {
      const data = await usageApi.me();
      setUsage(data);
    } catch {
      // Silently swallow — the banner is informational only
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(iv);
  }, [load]);

  // ── visibility guards ──────────────────────────────────────────────────────
  if (dismissed || !usage) return null;

  const { llm_spent_cents = 0, llm_cap_cents = -1, is_byok = false } = usage;

  if (is_byok)                              return null; // BYOK — no platform cap
  if (!llm_cap_cents || llm_cap_cents < 0)  return null; // unlimited / admin bypass

  const pct = Math.min(100, Math.round((llm_spent_cents / llm_cap_cents) * 100));
  if (pct < 80) return null;

  const isCritical = pct >= 95;
  const barColor   = isCritical ? '#ef4444' : 'hsl(28 90% 45%)';

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_PREFIX + monthKey(), '1');
    setDismissed(true);
  };

  return (
    <div
      data-testid="token-nudge-banner"
      className="flex items-center gap-2.5 border-b px-3 py-1.5"
      style={{
        background:   isCritical ? 'hsl(0 70% 45% / 0.10)' : 'hsl(28 90% 45% / 0.10)',
        borderColor:  isCritical ? 'hsl(0 70% 45% / 0.35)' : 'hsl(28 90% 45% / 0.30)',
      }}
    >
      {/* Icon */}
      <Zap
        className="h-3.5 w-3.5 shrink-0"
        style={{ color: barColor }}
        aria-hidden="true"
      />

      {/* Progress track */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="h-1.5 min-w-[60px] flex-1 overflow-hidden rounded-full bg-neutral-800">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: barColor }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${pct}% of monthly AI budget used`}
          />
        </div>
        <span
          className="shrink-0 text-[13.5px] font-bold tabular-nums"
          style={{ color: barColor }}
          data-testid="token-nudge-pct"
        >
          {pct}%
        </span>
        <span className="hidden shrink-0 text-[13px] text-neutral-500 sm:inline">
          monthly AI budget used
        </span>
      </div>

      {/* One-click top-up */}
      <button
        type="button"
        data-testid="token-nudge-topup-btn"
        onClick={() => navigate('/app?view=billing')}
        className="shrink-0 flex items-center gap-1 rounded-md px-2.5 py-1 text-[13.5px] font-bold text-white transition-opacity hover:opacity-90 active:scale-95"
        style={{ background: barColor }}
        aria-label="Top up AI budget"
      >
        <TrendingUp className="h-3 w-3" />
        Top Up
      </button>

      {/* Dismiss for the rest of this month */}
      <button
        type="button"
        data-testid="token-nudge-dismiss-btn"
        onClick={handleDismiss}
        className="shrink-0 text-neutral-600 transition-colors hover:text-neutral-300"
        aria-label="Dismiss token warning for this month"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
